// ─────────────────────────────────────────────────────────────────────────────
// MovementSystem — fachada de alto nível
//
// Junta o Loader + os MovementComponents + os BirdSpawnerManagers num único
// objeto que a engine cria uma vez e chama `update(now, viewport, cameraAngle)`
// a cada frame. Nada aqui conhece o jogador.
//
// Uso típico:
//   const mv = new MovementSystem();
//   await mv.init();                              // carrega os JSON
//   mv.addNpc('blacksmith', 'blacksmith_idle');   // id da entidade, id do perfil
//   mv.addNpc('guard_1', 'town_guard_patrol', { x: 4, y: 6 });
//   mv.addBirdSpawner('hub_birds');
//   // no loop:
//   mv.update(performance.now(), { width: W, height: H }, cameraAngleRad);
//   for (const npc of mv.npcs()) { ...desenha... }
//   for (const bird of mv.birds()) { ...desenha... }
// ─────────────────────────────────────────────────────────────────────────────

import { BirdSpawnerManager, Bird, Eagle } from './BirdSpawnerManager';
import { MovementComponent, Vec2 } from './MovementComponent';
import { MovementProfileLoader } from './MovementProfileLoader';
import { Viewport } from './schema';

export class MovementSystem {
  private loader = new MovementProfileLoader();
  private components = new Map<string, MovementComponent>();
  private spawners = new Map<string, BirdSpawnerManager>();
  private lastNow = 0;
  private ready = false;

  /** Carrega e valida os arquivos JSON. Chame antes de `addNpc`/`addBirdSpawner`. */
  async init(npcUrl?: string, birdsUrl?: string): Promise<void> {
    await this.loader.load(npcUrl, birdsUrl);
    this.ready = true;
  }

  isReady(): boolean { return this.ready; }

  /** Registra um NPC/patrulheiro. `startPos` opcional sobrescreve a posição inicial. */
  addNpc(entityId: string, profileId: string, startPos?: Vec2): MovementComponent {
    const profile = this.loader.getNpcProfile(profileId);
    const comp = new MovementComponent(entityId, profile, startPos);
    this.components.set(entityId, comp);
    return comp;
  }

  removeNpc(entityId: string): void {
    this.components.delete(entityId);
  }

  getNpc(entityId: string): MovementComponent | undefined {
    return this.components.get(entityId);
  }

  npcs(): IterableIterator<MovementComponent> {
    return this.components.values();
  }

  /** Ativa um spawner de pássaros pelo id do JSON. */
  addBirdSpawner(spawnerId: string): BirdSpawnerManager {
    const cfg = this.loader.getBirdSpawner(spawnerId);
    const mgr = new BirdSpawnerManager(spawnerId, cfg);
    this.spawners.set(spawnerId, mgr);
    return mgr;
  }

  removeBirdSpawner(spawnerId: string): void {
    this.spawners.delete(spawnerId);
  }

  birds(): Bird[] {
    const all: Bird[] = [];
    for (const s of this.spawners.values()) all.push(...s.getBirds());
    return all;
  }

  /** Águias caçando agora (de todos os spawners). */
  eagles(): Eagle[] {
    const all: Eagle[] = [];
    for (const s of this.spawners.values()) all.push(...s.getEagles());
    return all;
  }

  /** Zera pássaros vivos (ao trocar de mapa, por ex.). NPCs continuam. */
  clearBirds(): void {
    for (const s of this.spawners.values()) s.clear();
  }

  /**
   * Avança tudo. `now` em ms (use `performance.now()`); o `dt` é derivado
   * internamente e limitado a 100ms para sobreviver a travadas/abas em 2º plano.
   */
  update(now: number, viewport: Viewport, cameraAngle = 0): void {
    if (!this.lastNow) { this.lastNow = now; return; }
    const dt = Math.min(0.1, Math.max(0, (now - this.lastNow) / 1000));
    this.lastNow = now;

    for (const c of this.components.values()) c.update(dt);
    for (const s of this.spawners.values()) s.update(dt, viewport);

    // guarda o ângulo p/ quem chamar getFrame() sem argumento não precise dele
    this.cameraAngle = cameraAngle;
  }

  private cameraAngle = 0;

  /** Ângulo de câmera do último `update` — conveniência para o renderer. */
  getCameraAngle(): number { return this.cameraAngle; }
}

export type { Bird, Eagle } from './BirdSpawnerManager';
export type { Vec2 } from './MovementComponent';
